import 'react-datepicker/dist/react-datepicker.css'
import { Provider, h } from '@debbie/cortex/dist/index.js'
import { notifications } from '@debbie/cortex/dist/index.js'
import { useMe } from '@debbie/cortex/dist/me/index.js'
import { RootState } from '@debbie/cortex/dist/reducer.js'
import Authenticate from '@debbie/iris/dist/components/Authenticate.js'
import { PlaceholderLoader } from '@debbie/iris/dist/components/index.js'
import X from '@debbie/iris/dist/components/X.js'
import isPropValid from '@emotion/is-prop-valid'
import * as Sentry from '@sentry/browser'
import moment from 'moment'
import React from 'react'
import NotificationSystem from 'react-notification-system'
import { useSelector } from 'react-redux'
import { Route } from 'react-router-dom'
import { StyleSheetManager, styled } from 'styled-components'
import { StyledTarget } from 'styled-components/dist/types.js'

import logo from './assets/images/logoneg.svg'
import MeetingLoggedInView from './bespoke/mortang/LoggedInView.js'
import CollectorDNSSetup from './components/CollectorDNSSetup.js'
import ExperianClientNumber from './components/ExperianClientNumber.js'
import { ENABLE_SENTRY, ENV, SENTRY_DSN } from './config.js'
import LoggedInView from './LoggedInView.js'

if (ENABLE_SENTRY && SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    // Never send IP addresses or other default PII to Sentry (GDPR).
    sendDefaultPii: false,
    integrations: [
      Sentry.replayIntegration({
        // Mask all rendered text and input values and block all media before
        // anything leaves the browser. Do NOT disable or add unmask overrides —
        // debtor/creditor PII (names, amounts, CPR) must never reach Sentry.
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],
    // Record 10% of ordinary sessions and 100% of sessions that hit an error.
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  })
}

moment.locale()

const Wrapper = styled.div``

function shouldForwardProp(propName: string, target: StyledTarget<'web'>) {
  if (typeof target === 'string') {
    // For HTML elements, forward the prop if it is a valid HTML attribute
    return isPropValid(propName)
  }
  // For other elements, forward all props
  return true
}

const App = () => {
  return (
    <StyleSheetManager
      enableVendorPrefixes
      shouldForwardProp={shouldForwardProp}
    >
      <Wrapper>
        <Provider>
          <X
            logo={logo}
            loggedInView={
              <Authenticate>
                <LoginRouting />
              </Authenticate>
            }
            meetingView={<MeetingLoggedInView></MeetingLoggedInView>}
            routes={[
              <Route key="experian" path="/experian/:creditorId">
                <ExperianClientNumber />
              </Route>,
              <Route key="dns" path="/dns-setup/:tenantId/:collectorId">
                <CollectorDNSSetup />
              </Route>,
            ]}
          />
        </Provider>
      </Wrapper>

      <NotificationSystem
        ref={(r) => notifications.attachNotificationHandler(r)}
      />
    </StyleSheetManager>
  )
}

const LoginRouting = (props: {}) => {
  const me = useMe()
  const lang = useSelector((s: RootState) => s.lang)

  if (h.isError(me)) {
    return <p>{lang.misc.error}</p>
  }

  if (!h.hasData(me) || h.isError(me)) {
    return <PlaceholderLoader />
  }

  return h.hasData(me) &&
    me.data.details.meetingAccountType === 'meeting_only' ? (
    <MeetingLoggedInView />
  ) : (
    <LoggedInView />
  )
}

export default App
